import { supabase } from "../supabaseClient";

/**
 * Upload an article Markdown body directly from the browser to Cloudflare R2.
 *
 * The authenticated create-r2-upload Edge Function authorizes the upload and
 * chooses the article-scoped object key. The Markdown bytes then travel
 * directly from the browser to R2.
 */
export async function uploadArticleBodyToR2(markdownBody, articleId) {
  if (!articleId) {
    throw new Error("uploadArticleBodyToR2 requires an articleId");
  }

  const blob = new Blob([String(markdownBody || "")], {
    type: "text/markdown",
  });

  if (!blob.size) {
    throw new Error("Article body is empty");
  }

  const { data, error } = await supabase.functions.invoke(
    "create-r2-upload",
    {
      body: {
        contentType: "text/markdown",
        fileSize: blob.size,
        purpose: "article",
        articleId,
      },
    }
  );

  if (error) {
    console.error("Failed to authorize R2 article upload:", error);
    throw error;
  }

  if (!data?.success || !data?.uploadUrl || !data?.storage?.objectKey) {
    console.error("Invalid create-r2-upload response:", data);
    throw new Error(
      data?.error || "Unable to authorize article upload"
    );
  }

  const uploadResponse = await fetch(data.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "text/markdown",
    },
    body: blob,
  });

  if (!uploadResponse.ok) {
    const responseText = await uploadResponse.text().catch(() => "");

    console.error(
      "R2 article upload failed:",
      uploadResponse.status,
      responseText
    );

    throw new Error(
      `R2 article upload failed (${uploadResponse.status})`
    );
  }

  return {
    storage_provider: data.storage.provider || "r2",
    storage_bucket: data.storage.bucket,
    object_key: data.storage.objectKey,
    mime_type: "text/markdown",
    file_size_bytes: blob.size,
  };
}

/**
 * Delete an R2 article body through the authenticated server-side media
 * deletion endpoint. The Edge Function verifies article permissions and
 * constrains deletion to this article's own body namespace.
 */
export async function deleteArticleBodyFromR2(articleId, objectKey = null) {
  if (!articleId) {
    throw new Error("deleteArticleBodyFromR2 requires an articleId");
  }

  const body = {
    resourceType: "article",
    resourceId: articleId,
  };

  if (objectKey) {
    body.objectKey = objectKey;
  }

  const { data, error } = await supabase.functions.invoke(
    "delete-r2-media",
    { body }
  );

  if (error) {
    console.error("Failed to delete R2 article body:", error);
    throw error;
  }

  if (!data?.success) {
    console.error("Invalid delete-r2-media response:", data);
    throw new Error(data?.error || "Unable to delete R2 article body");
  }

  return data;
}

